import React, { useState, useRef } from 'react';
import { cardMarkup } from './gameHelpers';

const SWIPE_THRESHOLD = 80; // px to trigger an action

// A single deck of cards you cycle through (one on top) and commit. Works by
// button OR swipe: swipe right to commit the top card, swipe left to cycle.
const CardDeck = ({
  items,
  getText,
  onCommit,
  commitLabel,
  committingLabel,
  countLabel,
}) => {
  const [idx, setIdx] = useState(0);
  const [committing, setCommitting] = useState(false);
  const [offset, setOffset] = useState(0);
  const start = useRef(null);

  if (!items.length) return null;

  const pos = Math.min(idx, items.length - 1);
  const current = items[pos];
  const multiple = items.length > 1;

  const next = () => {
    setOffset(0);
    setIdx((i) => (i + 1) % items.length);
  };

  const commit = async () => {
    if (committing) return;
    setCommitting(true);
    setOffset(0);
    await onCommit(current);
    setCommitting(false);
  };

  const onPointerDown = (e) => {
    if (committing) return;
    start.current = e.clientX;
    if (e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  const onPointerMove = (e) => {
    if (start.current === null) return;
    setOffset(e.clientX - start.current);
  };

  const onPointerEnd = () => {
    if (start.current === null) return;
    const dx = offset;
    start.current = null;
    if (dx > SWIPE_THRESHOLD) {
      commit();
    } else if (dx < -SWIPE_THRESHOLD && multiple) {
      next();
    } else {
      setOffset(0);
    }
  };

  const dragging = start.current !== null;
  const topStyle = {
    transform: `translateX(${offset}px) rotate(${offset * 0.04}deg)`,
    transition: dragging ? 'none' : 'transform 0.25s ease',
    opacity: 1 - Math.min(Math.abs(offset) / 400, 0.4),
    touchAction: 'pan-y',
    cursor: committing ? 'default' : 'grab',
  };

  return (
    <div className="cardstack">
      <div className="cardstack__deck">
        {items.length > 2 && (
          <div className="card cardstack__behind cardstack__behind2" aria-hidden="true" />
        )}
        {multiple && (
          <div className="card cardstack__behind cardstack__behind1" aria-hidden="true" />
        )}
        <div
          className="card cardstack__top"
          style={topStyle}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          dangerouslySetInnerHTML={cardMarkup(getText(current))}
        />
      </div>

      <div className="cardstack__count">{countLabel(pos + 1, items.length)}</div>
      <div className="cardstack__hint">
        Swipe → to {commitLabel.toLowerCase()}
        {multiple ? ', ← for next' : ''}
      </div>

      <div className="cardstack__actions">
        <button className="btn" onClick={next} disabled={!multiple || committing}>
          Next Card
        </button>
        <button className="btn btn-gold" onClick={commit} disabled={committing}>
          {committing ? committingLabel : commitLabel}
        </button>
      </div>
    </div>
  );
};

export default CardDeck;
